import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toDateAsUTC(dateString: string): Date {
  // 日付文字列にTが含まれているかチェック
  if (dateString.includes("T")) {
    // ISO 8601形式の場合
    // 既にタイムゾーン情報（+00:00, -00:00, Z）が含まれている場合はそのまま使用
    if (
      dateString.includes("+") ||
      dateString.includes("-", 10) || // 日付部分の-以外の-をチェック
      dateString.endsWith("Z")
    ) {
      return new Date(dateString);
    }
    // タイムゾーン情報がない場合はUTCとして扱う
    return new Date(dateString + "Z");
  } else {
    // 日付のみの場合（例：2024-07-22）
    return new Date(dateString + "T00:00:00.000Z");
  }
}

export function formatAsJa(
  dateStrOrJstDate: string | Date | null | undefined
): string {
  if (!dateStrOrJstDate) {
    return "";
  }
  try {
    const date =
      typeof dateStrOrJstDate === "string"
        ? toDateAsUTC(dateStrOrJstDate)
        : dateStrOrJstDate;
    if (isNaN(date.getTime())) {
      return "";
    }
    return format(date, "yyyy年MM月dd日(eee) HH:mm:ss", {
      locale: ja,
    });
  } catch (error) {
    console.error("Error formatting date:", dateStrOrJstDate, error);
    return "";
  }
}

export function formatAsJaDate(
  dateStrOrJstDate: string | Date | null | undefined
): string {
  if (!dateStrOrJstDate) {
    return "";
  }
  try {
    const date =
      typeof dateStrOrJstDate === "string"
        ? toDateAsUTC(dateStrOrJstDate)
        : dateStrOrJstDate;
    if (isNaN(date.getTime())) {
      return "";
    }
    return format(date, "yyyy年MM月dd日(eee)", {
      locale: ja,
    });
  } catch (error) {
    console.error("Error formatting date:", dateStrOrJstDate, error);
    return "";
  }
}

/**
 * 勤務形態を英語から日本語に変換する関数
 * @param employmentType 勤務形態（FULL, PART, または日本語）
 * @returns 日本語の勤務形態（常勤、非常勤）または"-"
 */
export function formatEmploymentType(
  employmentType: string | null | undefined
): string {
  if (!employmentType) return "-";
  if (employmentType === "FULL") return "常勤";
  if (employmentType === "PART") return "非常勤";
  // 既に日本語の場合はそのまま返す（後方互換性のため）
  if (employmentType === "常勤" || employmentType === "非常勤") {
    return employmentType;
  }
  return employmentType;
}

/**
 * OCR結果やAPIレスポンスの勤務形態をフォーム用（FULL/PART）に変換する。
 * 未抽出・空欄の場合は defaultValue（既定: 常勤）を返す。
 */
export function parseEmploymentTypeForForm(
  employmentType: string | null | undefined,
  defaultValue: "FULL" | "PART" = "FULL",
): "FULL" | "PART" {
  if (employmentType === "FULL" || employmentType === "常勤") {
    return "FULL";
  }
  if (employmentType === "PART" || employmentType === "非常勤") {
    return "PART";
  }
  return defaultValue;
}

/**
 * 勤続期間を「〇年〇か月」形式で計算・フォーマットする
 * 勤務中（endDate が null）の場合は当日まで、勤務終了済みの場合は勤務終了日までで計算
 * @param startDate 勤務開始日（ISO形式文字列 "YYYY-MM-DD" 等）
 * @param endDate 勤務終了日（null の場合は当日を使用）
 * @returns "〇年〇か月" 形式の文字列。計算不能の場合は空文字
 */
export function formatEmploymentDuration(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined
): string {
  if (!startDate) return "";

  let start: Date;
  let end: Date;

  try {
    start =
      typeof startDate === "string"
        ? new Date(startDate.includes("T") ? startDate : startDate + "T12:00:00")
        : startDate;
    if (isNaN(start.getTime())) return "";

    if (endDate) {
      end =
        typeof endDate === "string"
          ? new Date(endDate.includes("T") ? endDate : endDate + "T12:00:00")
          : endDate;
    } else {
      // 勤務中：当日を使用
      end = new Date();
      end.setHours(12, 0, 0, 0);
    }
    if (isNaN(end.getTime())) return "";

    start.setHours(12, 0, 0, 0);
    end.setHours(12, 0, 0, 0);

    if (start > end) return "";

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    const days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}年${months}か月`;
  } catch {
    return "";
  }
}
